import * as apiPostProduct from 'api/erp/PostProduct';
import * as apiGetDetails from 'api/shoppingCart/GetShoppingCartGetDetailsIdV1';
import { Data as ReceiptData } from 'components/desktop/ShoppingCart/Detail/misc/Receipt';
import { Functor } from 'functions/Functor';
import * as ErpProductList from 'types/Recipe/Detail/ErpNew/ProductList';
import { Product } from 'types/ShoppingCart/Product';
import { Translation } from 'types/_general/Translation';

export * as ProductList from 'types/ShoppingCart/ProductList';

export type Type = Product.Type[];

export function create(): Type {
	return [];
}

export function search(list: Type, searchTerm: string, cultureCode: string): Type {
	return list.filter((e) => {
		const name = Translation.getFirstTranslation(e.ingredient.name, cultureCode);
		return name.toLowerCase().includes(searchTerm.toLowerCase());
	});
}

export function distinct(list: Type): Type {
	const output: Type = [];
	for (const i of list) {
		if (includes(output, i.id, i.store)) continue;
		output.push(i);
	}
	return output;
}

export function includes(list: Type, id: string, store: string): boolean {
	for (const i of list) {
		if (i.id === id && i.store === store) return true;
	}
	return false;
}

export async function enrichWithProductInformations(list: Type): Promise<Type> {
	const output: Type = [];
	const products: ErpProductList.Type = await Functor.From(list)
		.map(distinct)
		.map(mapToApi)
		.mapAsync(ErpProductList.getFromApi)
		.get();

	for (const i of list) {
		const product = products.find((e) => e.productId === i.id);
		if (!product) {
			output.push(i);
			continue;
		}
		output.push({
			...i,
			imageUrl: product.imageUrl,
			price: product.price ?? 0,
			gtin: product.gtin,
			nutrients: product.nutrients,
			size: (product.size && product.size * 1000) || 0,
		});
	}
	return output;
}

function mapToApi(list: Type): apiPostProduct.Request[] {
	const temp = list.map((e) => ({ productId: e.id, sourceId: e.store.toLowerCase() ?? '' }));
	return ErpProductList.mapToApi(temp);
}

export function getIds(list: Type): string[] {
	return list.map((e) => e.id);
}

export function getProductsFromAppearanceId(list: Type, id: string): Type {
	return list.filter((e) => e.source.id === id);
}

export function isAlreadyInList(list: Type, product: Product.Type): boolean {
	return list.map((e) => Product.getUniqId(e)).includes(Product.getUniqId(product));
}

export function findByUniqId(list: Type, product: Product.Type): Product.Type | undefined {
	return list.find((e) => Product.getUniqId(e) === Product.getUniqId(product));
}

export function mergeQuantity(obj: Type): Type {
	let output: Type = [];
	obj.forEach((product) => {
		if (isAlreadyInList(output, product)) {
			output = output.map((e) => {
				if (Product.getUniqId(e) !== Product.getUniqId(product)) return e;
				return { ...e, quantity: e.quantity + product.quantity };
			});
		} else {
			output.push(product);
		}
	});
	return output;
}

export function mapToReceiptData(obj: Type, cultureCode: string): ReceiptData[] {
	return obj.map((e) => Product.mapToReceiptData(e, cultureCode));
}

export function mapFromApi(list: Type, input: apiGetDetails.ProductItems[]): Type {
	return input.map((e) => {
		const product = list.find((x) => x.id === e.productId);
		if (!product) return Product.mapFromApi(e);
		return {
			...product,
			weight: e.weightOrVolume,
			source: e.source,
		};
	});
}

export function setQuantity(list: Type, id: string, quantity: number): Type {
	return list.map((e) => {
		if (e.id !== id) return e;
		return Product.setQuantity(e, quantity);
	});
}
export function group(list: Type): Type {
	const output: Type = [];
	for (const i of list) {
		let found: boolean = false;
		for (const z of output) {
			if (z.id !== i.id || z.store !== i.store) continue;
			(z.weight = z.weight + i.weight), (found = true);
		}
		if (!found) output.push({ ...i });
	}
	return output;
}
